/*
 * Quantellia DIModelConverter.
 *
 * Converts json between internal .vis format and .odi standard decision model
 * (doesn't handle file I/O)
 *
 * Copyright (c) 2016 - 2025 Quantellia LLC
 * 
 *  All Rights Reserved.
 * 
 * NOTICE:  All source code, data, and any other information contained 
 * in this file is, and remains the property of Quantellia LLC.
 * The intellectual and technical concepts contained
 * herein are proprietary to Quantellia LLC and may be covered by
 * U.S. and Foreign Patents, patents in process, and are protected 
 * by trade secret or copyright law. Dissemination of this information 
 * or reproduction of this material is strictly forbidden unless 
 * prior written permission is obtained from Quantellia LLC.
 * 
 * The above notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 *
 */

import {ODIModel} from "./odimodel.js";
import {VisModel} from "./vismodel.js";
import { createGUID } from "./qlib.js";
import { createNodeLabel  } from "../../../shared.js";

/**
 * DIModelConverter provides a set of static functions for converting between various Decision Intelligence
 * Model forats and other formats that represent node-link graphs that can be used to represent dependency networks.
 * 
 * Currently supported formats are:
 * - Open Decision Intelligence
 * - Vis.js (see https://github.com/visjs) 
 */
export class DIModelConverter
{

    static defaultElementWidth = 100;
    static defaultElementHeight = 25;
    static defaultFont = {
                            multi: "html",
                            size: 18,
                            boldital: { color: "darkblue"}
                        };

    /**
     * Private helper function for creating model element objects.
     * @param {object} node The source node being read
     * @param {object} odiModel The model being populated
     * @param {object} odiDiagram THe diagram in the model being populated.
     */
    static #_CreateODIElement(node, odiModel, odiDiagram)
    {
        let element = odiModel.addElement(
            {
                ID: node.id,
                Type: "DIEvaluatableElement", 
                Name: node.elementname,
                Summary: node.label,
                Role: odiModel.translateRole(node.elementtype)
            }
        );

        //
        // Add the visual representation of this element
        //
        odiDiagram.DiagramElements.push(
                    {
                        Type:"DIRectangleDiagramElement",
                        ID: createGUID(),
                        RepresentedElementRef: element.ID,
                        Position: 
                        {   X: node.x,
                            Y: node.y
            },
            Rotation: 0.0,
            Scale:
            {
              X: 1.0,
              Y: 1.0
            },
            Width: this.defaultElementWidth,
            Height: this.defaultElementHeight,
            BoundingBox:
            {
              Left: node.x,
              Top: node.y,
              Right: node.x + this.defaultElementWidth,
              Bottom: node.y + this.defaultElementWidth
            },
            SVG: "<rect \"x\":" + node.x + " \"y\":" + node.y + " \"width\":" + (this.defaultElementWidth) + " \"height\":" + (this.defaultElementHeight) + ">",
            ElementType: element.Role
          }
        );
  }

  /**
   * Private helper function for creating dependency objects.
   * @param {object} edge The source edge being read
   * @param {object} odiModel The model being populated
   * @param {object} odiDiagram THe diagram in the model being populated.
   */
  static #_CreateODIDependency(edge, odiModel, odiDiagram) {
    odiModel.addElement(
      {
        Type: "DIDependency",
        ID: edge.ID,
        Name: edge.ID,
        Role: "Dependency",
        Source: edge.from,
        Target: edge.to
      }
    );
  }

  /**
   * Convert the JSON representation of a graph in the format used by the Vis application to the
   * equivalent decision model in the JSON format defined by the Open Decision Intelligence standard.
   * @param {string} jVisModel  A JSON string that contains a node-link model as defined by Vis.js
   * @returns {string} A JSON string that contains the equivalent model to that provided in the input, formatted according to the Open DI  standard.
   */
  static VisToODI(jVisModel) {
    try {
      //
      // Make sure we have valid input
      //
      if (!jVisModel || jVisModel.length == 0)
        throw new Error("The supplied string was null or empty.");

      //
      // Trun the JSON string into an object.
      //
      let visModel = JSON.parse(jVisModel);

      //
      // Create the ODI model
      //
      let odiModel = new ODIModel();

      //
      // Create a single diagram and add it to the ODI model
      //
      let odiDiagram = odiModel.addDiagram();

      //
      // Iterate through the elements (nodes) of the Vis graph
      // and create ODI elements from them.
      //
      for (let i = 0; i < visModel.nodes.length; ++i) {
        let node = visModel.nodes[i];

        //
        // ID must be defined or we cannot form relationships.
        //
        if (!node.id || node.id.length == 0)
          throw new Error("Node at position " + i.toString() + " does not have a valid id. Processing stopped.");

        //
        // Create the model element and its visualization and add them to the model.
        //
        this.#_CreateODIElement(node, odiModel, odiDiagram);
      }

      //
      // Iterate through the edges and create their equivalent dependencies in the ODI model
      //
      for (let i = 0; i < visModel.edges.length; ++i) {
        let edge = visModel.edges[i];
        this.#_CreateODIDependency(edge, odiModel, odiDiagram);
      }


      return odiModel.toJSON();
    }
    catch (ex) {
      throw new Error("Error in DiModelConverter.VisToODI: " + ex.message);
    }

  }


  /**
  * Convert the JSON representation of a decision model in the JSON format defined by the Open Decision Intelligence standard
  * to the equivalent graph in the JSON  the format used by the Vis application.
  * @param {string} jODIModel  A JSON string that contains an ODI-compliant decision model.
  * @returns {string} A JSON string that contains the equivalent model to that provided in the input as represented in the Vis.js application.
  */
  static ODIToVis(jODIModel) {
    try {
      //
      // Make sure we have valid input
      //
      if (!jODIModel || jODIModel.length == 0)
        throw new Error("The supplied string was null or empty.");

      //
      // Trun the JSON string into an object.
      //
      let odiModel = JSON.parse(jODIModel);
      let odiElements = odiModel.Elements;
      let odiDiagram = odiElements.find((e) => e.Type == "DIModelDiagram");

      //
      // Create the Vis model
      //
      let visModel = new VisModel();


      //
      // Iterate through the elements (nodes) of the ODI model
      // and create Vis elements from them.
      //
      for (let i = 0; i < odiElements.length; ++i) {
        let element = odiElements[i];

        //
        // If this is a dependency, create a new edge
        //
        if (element.Type == "DIDependency")
          visModel.addEdge(
            {
              id: element.ID,
              from: element.Source,
              to: element.Target
            }
          );

        else if (element.Type != "DIModelDiagram") {
          //
          // Get the visual element that represents this element in the diagram
          //
          var odiDiagmElem = odiDiagram.DiagramElements.find((e) => e.RepresentedElementRef == element.ID);
          if (odiDiagmElem) {
            // visModel.addNode(
            var newNode = 
              {
                id: element.ID,
                x: odiDiagmElem.Position.X,
                y: odiDiagmElem.Position.Y,
                label: element.Summary, // Note this is about to be overwritten. TODO: what is the meaning of the Summary field to MZ?
                fixed: true,
                elementtype: element.Role,
                elementname: element.Name,
                font: this.defaultFont
              };
              // TODO note that the other invocations of createNodeLabel modify
              // node.options. Should I be doing that here? What is node.options.label,
              // as opposed to node.label?
              newNode = createNodeLabel(newNode); // Cosntruct the label
              visModel.addNode(newNode);
          }
          else {
              console.error("Problem with odi file: missing representedElement ref for ID: "+element.ID)
          }
        }
      }

      return visModel.toJSON();
    }
    catch (ex) {
      throw new Error("Error in DiModelConverter.ODIToVis: " + ex.message);
    }
  }
}